import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Users } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useBacklogCount, useBacklogSweep } from "@/hooks/sms/useBacklogSweep";
import { useInviteRotation } from "@/hooks/sms/useInviteRotation";
import { useInviteBatches } from "@/hooks/sms/useInviteBatches";
import { useMarketingSettings } from "@/hooks/sms/useMarketingSettings";
import {
  DEFAULT_BACKLOG_BATCH_SIZE,
  backlogSweepGate,
  isBacklogSweepMarked,
  localDate,
  type BacklogBlockedReason,
} from "@/utils/smsShared";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const BLOCKED_COPY: Partial<Record<BacklogBlockedReason, string>> = {
  kill_switch: "Paused by the emergency stop — turn it off in Settings first.",
  rotation_off: "Order Invite is off — turn it on to invite past customers.",
  weekend: "Past-customer invites only go out on weekdays.",
  already_ran_today: "Already ran today — the next batch can go out next weekday.",
};

/**
 * "Invite past customers" — the backlog sweep control. The daily rotation
 * only invites customers who ordered yesterday; this works through
 * everyone else who is invitable, one batch of up to 150 per weekday, added to
 * today's invite batch. Behind an AlertDialog stating batch size and cadence.
 * Mirrors the server's refusals (emergency stop, Order Invite off, weekend,
 * already ran today) with the specific reason beside the button.
 */
export function BacklogSweepCard({ paused }: { paused: boolean }) {
  const { data: backlogCount = 0, isLoading } = useBacklogCount();
  const { data: rotation } = useInviteRotation();
  const { data: settings } = useMarketingSettings();
  const { data: batches } = useInviteBatches();
  const sweep = useBacklogSweep();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  const batchSize = Math.min(DEFAULT_BACKLOG_BATCH_SIZE, backlogCount);

  const timezone = settings?.timezone ?? "America/Los_Angeles";
  const now = new Date();
  const todayLocal = localDate(now, timezone);
  const weekday = WEEKDAYS.indexOf(new Intl.DateTimeFormat("en-US", { timeZone: timezone, weekday: "short" }).format(now));
  const todayRow = batches?.find((b) => b.invite_occurrence_date === todayLocal);
  const blockedReason = backlogSweepGate({
    killSwitch: paused,
    rotationEnabled: rotation?.enabled === true,
    localWeekday: weekday,
    alreadyRanToday: isBacklogSweepMarked(todayRow?.audience_filter),
  });
  const blockedCopy = blockedReason ? BLOCKED_COPY[blockedReason] : null;

  const runSweep = () => {
    setFeedback(null);
    sweep.mutate(undefined, {
      onSuccess: (r) => setFeedback(`Queued ${r.queued} invite${r.queued === 1 ? "" : "s"} to past customers.`),
      onError: (e) => setFeedback(e.message),
    });
  };

  return (
    <Card>
      <CardContent className="space-y-3 p-4 sm:p-6">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <Users className="h-[18px] w-[18px]" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-foreground">Invite past customers</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {isLoading
                ? "Counting past customers…"
                : backlogCount === 0
                  ? "No past customers are waiting to be invited right now."
                  : `${backlogCount.toLocaleString()} past customer${backlogCount === 1 ? "" : "s"} could be invited beyond today's orders.`}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              One batch of up to {DEFAULT_BACKLOG_BATCH_SIZE} per weekday, only while Order Invite is on. Each past
              customer is invited once.
            </p>
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          className="h-11 w-full sm:h-10"
          disabled={blockedReason !== null || backlogCount === 0 || sweep.isPending}
          onClick={() => setDialogOpen(true)}
        >
          {sweep.isPending ? "Sending…" : `Invite up to ${batchSize || DEFAULT_BACKLOG_BATCH_SIZE} now`}
        </Button>
        {blockedCopy && (
          <p className={`text-xs ${blockedReason === "kill_switch" ? "text-status-danger" : "text-muted-foreground"}`}>
            {blockedCopy}
          </p>
        )}
        {feedback && <p className="text-xs text-muted-foreground">{feedback}</p>}
      </CardContent>

      <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Invite past customers?</AlertDialogTitle>
            <AlertDialogDescription>
              This texts one invite to up to {batchSize || DEFAULT_BACKLOG_BATCH_SIZE} invitable past customers, added to
              today's batch. It can run once per weekday, so the backlog is worked through without over-texting.
              Customers who opted out, already joined, or were invited before are never included.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={runSweep}>Invite them</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
